import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, Trash2, Edit3, X, CheckCircle, Clock, XCircle, Calendar, RefreshCw, CalendarCheck, CalendarX } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("clinic_admin_token")}` };
}

const STATUS_OPTIONS = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_STYLES = {
  pending: "bg-amber-500/20 text-amber-300 border border-amber-500/30",
  confirmed: "bg-blue-500/20 text-blue-300 border border-blue-500/30",
  completed: "bg-green-500/20 text-green-300 border border-green-500/30",
  cancelled: "bg-red-500/20 text-red-300 border border-red-500/30",
};
const STATUS_ICONS = { pending: Clock, confirmed: CheckCircle, completed: CheckCircle, cancelled: XCircle };

const emptyForm = {
  patient_name: "", patient_phone: "", patient_email: "",
  doctor_name: "", appointment_date: "", appointment_time: "",
  reason: "", notes: "", source: "manual",
};

export default function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Calendar integration state
  const [calendarSettings, setCalendarSettings] = useState({ google_calendar_id: null, calendly_api_key: null });
  const [syncingIds, setSyncingIds] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);

  useEffect(() => { fetchAppointments(); fetchCalendarSettings(); }, []);

  const fetchAppointments = async () => {
    try {
      const { data } = await axios.get(`${API}/appointments`, { headers: authHeader() });
      setAppointments(data);
    } catch { toast.error("Failed to load appointments"); }
    finally { setLoading(false); }
  };

  const fetchCalendarSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/settings`, { headers: authHeader() });
      setCalendarSettings({
        google_calendar_id: data.google_calendar_id || null,
        calendly_api_key: data.calendly_api_key || null,
      });
    } catch { /* silently ignore */ }
  };

  const syncToCalendar = async (id) => {
    setSyncingIds(prev => ({ ...prev, [id]: true }));
    try {
      await axios.post(`${API}/appointments/${id}/sync-calendar`, {}, { headers: authHeader() });
      toast.success("Appointment synced to calendar");
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, calendar_synced: true } : a));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync appointment");
    } finally {
      setSyncingIds(prev => ({ ...prev, [id]: false }));
    }
  };

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      await axios.post(`${API}/appointments/sync-all-calendar`, {}, { headers: authHeader() });
      toast.success("All appointments synced to calendar");
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync all appointments");
    } finally {
      setSyncingAll(false);
    }
  };

  const openCreate = () => { setForm(emptyForm); setEditId(null); setShowModal(true); };
  const openEdit = (apt) => {
    setForm({
      patient_name: apt.patient_name || "",
      patient_phone: apt.patient_phone || "",
      patient_email: apt.patient_email || "",
      doctor_name: apt.doctor_name || "",
      appointment_date: apt.appointment_date || "",
      appointment_time: apt.appointment_time || "",
      reason: apt.reason || "",
      notes: apt.notes || "",
      source: apt.source || "manual",
    });
    setEditId(apt.id);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await axios.patch(`${API}/appointments/${editId}`, form, { headers: authHeader() });
        toast.success("Appointment updated");
      } else {
        await axios.post(`${API}/appointments`, form, { headers: authHeader() });
        toast.success("Appointment created");
      }
      setShowModal(false);
      fetchAppointments();
    } catch { toast.error("Failed to save appointment"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await axios.delete(`${API}/appointments/${id}`, { headers: authHeader() });
      toast.success("Appointment deleted");
      setAppointments(prev => prev.filter(a => a.id !== id));
    } catch { toast.error("Failed to delete"); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.patch(`${API}/appointments/${id}`, { status }, { headers: authHeader() });
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      toast.success("Status updated");
    } catch { toast.error("Failed to update status"); }
  };

  const filtered = appointments.filter(a => {
    const matchSearch = !search || a.patient_name?.toLowerCase().includes(search.toLowerCase()) || a.patient_phone?.includes(search);
    const matchStatus = !filterStatus || a.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const googleConnected = !!calendarSettings.google_calendar_id;
  const calendlyConnected = !!calendarSettings.calendly_api_key;
  const anyCalendarConnected = googleConnected || calendlyConnected;

  return (
    <div className="space-y-5 bg-[#0F1117] min-h-screen p-5" data-testid="appointments-page">
      {/* Integrations Banner */}
      <div className="flex items-center gap-4 bg-[#161B27] border border-white/10 rounded-xl px-5 py-3">
        <Calendar className="w-4 h-4 text-cyan-400 flex-shrink-0" />
        <span className="text-sm text-slate-400 font-medium mr-1">Calendar Integrations:</span>
        <div className="flex items-center gap-3">
          <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
            googleConnected
              ? "bg-green-500/15 text-green-400 border border-green-500/25"
              : "bg-white/5 text-slate-500 border border-white/10"
          }`}>
            {googleConnected
              ? <CheckCircle className="w-3 h-3" />
              : <XCircle className="w-3 h-3" />
            }
            Google Calendar {googleConnected ? "✓" : "—"}
          </span>
          <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
            calendlyConnected
              ? "bg-green-500/15 text-green-400 border border-green-500/25"
              : "bg-white/5 text-slate-500 border border-white/10"
          }`}>
            {calendlyConnected
              ? <CheckCircle className="w-3 h-3" />
              : <XCircle className="w-3 h-3" />
            }
            Calendly {calendlyConnected ? "✓" : "—"}
          </span>
        </div>
        {!anyCalendarConnected && (
          <span className="text-xs text-slate-500 ml-auto">
            Connect calendars in <span className="text-cyan-400">Settings → Integrations</span>
          </span>
        )}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-outfit text-white">Appointments</h1>
          <p className="text-slate-400 text-sm">{appointments.length} total appointments</p>
        </div>
        <div className="flex items-center gap-2">
          {anyCalendarConnected && (
            <button
              data-testid="sync-all-calendar-btn"
              onClick={syncAll}
              disabled={syncingAll}
              className="flex items-center gap-2 bg-cyan-600/20 border border-cyan-500/30 text-cyan-400 px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-600/30 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${syncingAll ? "animate-spin" : ""}`} />
              {syncingAll ? "Syncing..." : "Sync All"}
            </button>
          )}
          <button
            data-testid="create-appointment-btn"
            onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> New Appointment
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="appointment-search"
            type="text"
            placeholder="Search by name or phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          data-testid="appointment-status-filter"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-[#0A0D14] border border-white/10 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Status</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-[#161B27] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/5 border-b border-white/10">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Patient</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Doctor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date & Time</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reason</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sync</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-4 py-3"><div className="h-4 bg-white/5 rounded w-32" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-white/5 rounded w-24" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-white/5 rounded w-28" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-white/5 rounded w-20" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-white/5 rounded w-16" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-white/5 rounded w-12" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-white/5 rounded w-16" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-white/5 rounded w-16 ml-auto" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-slate-500">No appointments found</td></tr>
              ) : (
                filtered.map(apt => {
                  const Icon = STATUS_ICONS[apt.status] || Clock;
                  const isSyncing = syncingIds[apt.id];
                  return (
                    <tr key={apt.id} className="hover:bg-white/5 transition-colors" data-testid={`appointment-row-${apt.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">{apt.patient_name}</div>
                        <div className="text-xs text-slate-500">{apt.patient_phone}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-400">{apt.doctor_name || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-300">{apt.appointment_date}</div>
                        <div className="text-xs text-slate-500">{apt.appointment_time}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-400 max-w-32 truncate">{apt.reason || "—"}</td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`status-select-${apt.id}`}
                          value={apt.status}
                          onChange={e => handleStatusChange(apt.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer ${STATUS_STYLES[apt.status]} bg-transparent`}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-white/10 text-slate-400 px-2 py-0.5 rounded-full">{apt.source}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {apt.calendar_synced ? (
                            <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-2 py-0.5 rounded-full">
                              <CalendarCheck className="w-3 h-3" /> Synced
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-slate-500 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
                              <CalendarX className="w-3 h-3" /> Not synced
                            </span>
                          )}
                          {anyCalendarConnected && (
                            <button
                              data-testid={`sync-calendar-${apt.id}`}
                              onClick={() => syncToCalendar(apt.id)}
                              disabled={isSyncing}
                              className="p-1 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded transition-colors disabled:opacity-50"
                              title="Sync to calendar"
                            >
                              <RefreshCw className={`w-3 h-3 ${isSyncing ? "animate-spin" : ""}`} />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            data-testid={`edit-appointment-${apt.id}`}
                            onClick={() => openEdit(apt)}
                            className="p-1.5 text-slate-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-md transition-colors"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`delete-appointment-${apt.id}`}
                            onClick={() => handleDelete(apt.id)}
                            className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-[#161B27] border border-white/10 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-lg font-semibold font-outfit text-white">
                {editId ? "Edit Appointment" : "New Appointment"}
              </h2>
              <button data-testid="close-modal-btn" onClick={() => setShowModal(false)} className="text-slate-500 hover:text-slate-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1">Patient Name *</label>
                  <input data-testid="form-patient-name" required value={form.patient_name} onChange={e => setForm(p => ({ ...p, patient_name: e.target.value }))} className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1">Phone *</label>
                  <input data-testid="form-patient-phone" required value={form.patient_phone} onChange={e => setForm(p => ({ ...p, patient_phone: e.target.value }))} className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1">Email</label>
                  <input data-testid="form-patient-email" type="email" value={form.patient_email} onChange={e => setForm(p => ({ ...p, patient_email: e.target.value }))} className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1">Doctor</label>
                  <input data-testid="form-doctor-name" value={form.doctor_name} onChange={e => setForm(p => ({ ...p, doctor_name: e.target.value }))} className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1">Date *</label>
                  <input data-testid="form-date" required type="date" value={form.appointment_date} onChange={e => setForm(p => ({ ...p, appointment_date: e.target.value }))} className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1">Time *</label>
                  <input data-testid="form-time" required type="time" value={form.appointment_time} onChange={e => setForm(p => ({ ...p, appointment_time: e.target.value }))} className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Reason for Visit</label>
                <input data-testid="form-reason" value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 block mb-1">Notes</label>
                <textarea data-testid="form-notes" rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-white/10 rounded-lg text-sm text-slate-400 hover:bg-white/5 transition-colors">
                  Cancel
                </button>
                <button data-testid="save-appointment-btn" type="submit" disabled={saving} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
                  {saving ? "Saving..." : (editId ? "Update" : "Create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
